import { useEffect, useCallback, useSyncExternalStore } from 'react'
import { useLocation } from 'react-router-dom'

// UTM parameter names
const UTM_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
]
const STORAGE_KEY = 'claims1_utm_params'

const isBrowser = () =>
  typeof window !== 'undefined' && typeof localStorage !== 'undefined'

// Lets components that compute hrefs at render time (e.g. UtmLink) re-render
// as soon as the landing page's UTMs are captured, instead of staying stale
// until some unrelated navigation happens to trigger a re-render.
const listeners = new Set()
const notifyUtmChange = () => listeners.forEach(listener => listener())
const subscribeToUtmChanges = callback => {
  listeners.add(callback)
  return () => listeners.delete(callback)
}
const getUtmSnapshot = () =>
  isBrowser() ? localStorage.getItem(STORAGE_KEY) || '' : ''
const getUtmServerSnapshot = () => ''

// Function to get UTM parameters from URL
const getUtmParametersFromUrl = searchParams => {
  const utmParams = {}
  UTM_PARAMS.forEach(param => {
    if (searchParams.has(param)) {
      utmParams[param] = searchParams.get(param)
    }
  })
  return utmParams
}

// Function to retrieve UTM parameters from localStorage
const getStoredUtmParameters = () => {
  if (!isBrowser()) return {}

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

// Function to store UTM parameters in localStorage, merging with any already stored
const storeUtmParameters = utmParams => {
  if (!isBrowser() || Object.keys(utmParams).length === 0) return

  const existing = getStoredUtmParameters()
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({ ...existing, ...utmParams })
  )
  notifyUtmChange()
}

// Function to append UTM parameters to a URL (preserves existing parameters)
const appendUtmToUrl = (url, utmParams) => {
  if (!isBrowser() || !url || Object.keys(utmParams).length === 0) return url

  try {
    const urlObj = new URL(url, window.location.origin)

    // Only add UTM parameters that don't already exist in the URL
    Object.keys(utmParams).forEach(param => {
      if (!urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, utmParams[param])
      }
    })

    return urlObj.toString()
  } catch {
    return url
  }
}

export const useStoredUtmsVersion = () =>
  useSyncExternalStore(
    subscribeToUtmChanges,
    getUtmSnapshot,
    getUtmServerSnapshot
  )

// Custom hook for UTM tracking
export const useUtmTracking = () => {
  const location = useLocation()

  // Process UTM parameters on location change
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const currentUtms = getUtmParametersFromUrl(searchParams)

    // Store any new UTMs from the current URL
    storeUtmParameters(currentUtms)
  }, [location])

  // Function to get current stored UTM parameters
  const getStoredUtms = useCallback(() => {
    return getStoredUtmParameters()
  }, [])

  // Function to create a URL with UTM parameters appended
  const createUrlWithUtms = useCallback(url => {
    const storedUtms = getStoredUtmParameters()
    return appendUtmToUrl(url, storedUtms)
  }, [])

  // Function to update all links on the page with UTM parameters
  const updateLinksWithUtms = useCallback(() => {
    if (!isBrowser()) return

    const storedUtms = getStoredUtmParameters()

    if (Object.keys(storedUtms).length === 0) return

    // Update internal links (starting with /), excluding downloads
    const internalLinks = document.querySelectorAll(
      'a[href^="/"]:not([download])'
    )
    internalLinks.forEach(link => {
      const currentHref = link.getAttribute('href')
      const updatedHref = appendUtmToUrl(currentHref, storedUtms)
      link.setAttribute('href', updatedHref)
    })

    // Update external links to app.ycmcarfinanceclaims.co.uk
    const appLinks = document.querySelectorAll(
      'a[href*="app.ycmcarfinanceclaims.co.uk"]'
    )
    appLinks.forEach(link => {
      const currentHref = link.getAttribute('href')
      const updatedHref = appendUtmToUrl(currentHref, storedUtms)
      link.setAttribute('href', updatedHref)
    })
  }, [])

  return {
    getStoredUtms,
    createUrlWithUtms,
    updateLinksWithUtms,
  }
}

// Utility functions for direct use
export const utmUtils = {
  getStoredUtmParameters,
  appendUtmToUrl,
  createUrlWithUtms: url => {
    const storedUtms = getStoredUtmParameters()
    return appendUtmToUrl(url, storedUtms)
  },
}
